import {
  faCheckCircle,
  faExclamationCircle,
  faExclamationTriangle,
  faFlag,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import ChakraAlert from '~/common/components/Alerts/Alert/Alert';
import AlertDescription from '~/common/components/Alerts/AlertDescription';
import AlertTitle from '~/common/components/Alerts/AlertTitle';
import Box from '~/common/components/Box';
import Notice from '~/common/mui/MUINotice/Notice';

import { ALERT_SEVERITY } from '../../../constants';

type AlertProps = Omit<React.ComponentProps<typeof ChakraAlert>, 'status'> & {
  /** The alert title, or undefined for a default value based on the status type. */
  title?: string;
  /** The alert status type. */
  status?:
    | 'warning'
    | 'info'
    | 'error'
    | 'success'
    | number
    | (string & Record<never, never>);
  isNotice?: boolean;
};

const Alert = ({
  title,
  status,
  children,
  isNotice,
  sx,
  ...props
}: Readonly<AlertProps> & { sx?: any }) => {
  if (isNotice) {
    return <Notice severity={status as any} message={children} sx={sx} />;
  }

  let alertColor: string,
    alertIcon: any,
    alertBackground: string,
    borderColor: string;
  switch (status) {
    case 1:
    case 'info':
      status = 'info';
      alertColor = 'blue.600';
      alertBackground = 'blue.50';
      alertIcon = faInfoCircle;
      title = title || 'Tip:';
      break;
    case 2:
    case 'warning':
      status = 'warning';
      alertColor = 'orange.700';
      alertBackground = 'orange.100';
      alertIcon = faExclamationTriangle;
      title = title || 'New Condition:';
      break;
    case 'error':
      status = 'error';
      alertColor = 'red.700';
      alertBackground = 'red.100';
      alertIcon = faExclamationCircle;
      title = title || 'Error:';
      break;
    case 'success':
      status = 'success';
      alertColor = 'green.700';
      alertBackground = 'green.50';
      alertIcon = faCheckCircle;
      title = title || 'All Set:';
      break;
    case 'extractionFailed':
    case ALERT_SEVERITY.ATTENTION:
      status = undefined;
      alertBackground = 'orange.300';
      borderColor = 'orange.900';
      alertIcon = faFlag;
      title = title || 'Manual Review:';
      break;
    default:
      status = 'info';
      alertColor = 'blue.700';
      alertBackground = 'blue.50';
      alertIcon = faInfoCircle;
      title = title || 'Tip:';
      break;
  }
  return (
    <ChakraAlert
      border="1px solid"
      borderColor={status === 'extractionFailed' ? borderColor : alertColor}
      background={alertBackground}
      borderRadius="4px"
      {...props}
      sx={sx}
    >
      <Box color={`${alertColor}`} mr={2}>
        <FontAwesomeIcon icon={alertIcon} />
      </Box>
      {title && (
        <AlertTitle mr={4} fontSize="sm" color={alertColor} whiteSpace="nowrap">
          {title}
        </AlertTitle>
      )}
      <AlertDescription fontSize="sm" lineHeight="24px">
        {children}
      </AlertDescription>
    </ChakraAlert>
  );
};
export default Alert;
